import {isUndefined} from 'lodash';
import React, {FC, PropsWithChildren, ReactNode} from 'react';
import styled from 'styled-components';

import {buildInteractionIdProps} from '../../../../libs/shared/helpers/src/interaction/interactionHelpers';
import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {Handler, Renderer, RendererOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {AnalyticsMetricsKeys} from '../../../models/api/analyticsMetricsModel';
import {InterfaceEventsEnum} from '../../../services/tracking';
import {ReportElementIdsEnum} from '../../helpers/analyticsReportHelpers';
import {maybeRenderMetricPillAndTooltip} from '../../helpers/metricLabels';
import {useAnalyticsTracking} from '../../helpers/tracking/useAnalyticsTracking';
import {
  AnalyticsComputedGraphMetricsEnum,
  AnalyticsComputedProgressMetricsEnum,
} from '../../transformers/analyticsMetricsTransformersTypes';
import {ReportGridItem} from '../reportViewer/reportGrid';
import {EmptyStateRenderer} from './charts/emptyState';
import {AnalyticsHelpTooltip, AnalyticsTooltip} from './tooltips/analyticsTooltips';

/*
 * Props.
 */

type AnalyticsMetrics =
  | AnalyticsMetricsKeys
  | AnalyticsComputedGraphMetricsEnum
  | AnalyticsComputedProgressMetricsEnum;

export interface ReportElementProps<T extends AnalyticsMetrics> {
  isLoading: boolean;
  metric: T;
  renderEmptyState?: EmptyStateRenderer;
}

export interface ReportElementMultiMetricsProps<T extends AnalyticsMetrics> {
  isLoading: boolean;
  availableMetrics: ReadonlyArray<T>; // @TODO availableMetrics should be get from the report config instead of being passed.
  renderEmptyState?: EmptyStateRenderer;
}

export interface ReportElementWrapperProps {
  reportSectionId: ReportElementIdsEnum; // Used for tracking purpose and to save the list of selected metrics for each report element.
  title?: ReactNode; // Title must be a string if you need to pass it through to the metric details header.
  subtitle?: ReactNode;
  infoTooltip?: AnalyticsHelpTooltip;
  renderCustom?: Renderer;
  isExportable?: boolean;
  toggleExportDialog?: Handler;
  renderSettingsDropdown?: RendererOf<PopoverTransferProps>;
  width?: number;
  hasFullHeight?: boolean;
  isBeta?: boolean;
  isNew?: boolean;
}

/*
 * Styles.
 */

// Using these two helpers to not add the 8px gap if there are no "export" or "settings" icons.
const addGridTemplateColumnsStyles = (hasExport: boolean, hasSettings: boolean) =>
  `auto 8px auto 8px auto 1fr auto ${hasExport ? '8px auto' : ''} ${hasSettings ? '8px auto' : ''}`;

const addGridTemplateAreasStyles = (hasExport: boolean, hasSettings: boolean) =>
  `'title . info . pill . custom ${hasExport ? '. export' : ''} ${hasSettings ? '. settings' : ''}'`;

// @TODO: We'll need to support the search bar for the table.
const StyledReportElementTitleWrapper = styled.div<{
  $hasExport: boolean;
  $hasSettings: boolean;
}>`
  display: grid;
  grid-template-columns: ${(p) => addGridTemplateColumnsStyles(p.$hasExport, p.$hasSettings)};
  grid-template-areas: ${(p) => addGridTemplateAreasStyles(p.$hasExport, p.$hasSettings)};

  align-items: center;

  height: 32px;
`;

const StyledReportElementTitle = styled.span`
  grid-area: title;

  font-size: ${fontSizes.large};
  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.bold};
`;

const StyledReportElementInfoButton = styled(AnalyticsTooltip)`
  grid-area: info;
`;

const StyledReportElementCustomWrapper = styled.div`
  grid-area: custom;
`;

const StyledReportElementSubtitle = styled.div`
  font-size: ${fontSizes.medium};
  color: ${(p) => p.theme.greys.shade70};
  margin-bottom: 14px;
  line-height: 21px;
`;

const StyledReportElementExportButtonWrapper = styled.div`
  grid-area: export;
`;

const StyledReportElementSettingsButton = styled(PopoverCoordinator)`
  grid-area: settings;
`;

/*
 * Component.
 */

export const ReportElementWrapper: FC<PropsWithChildren<ReportElementWrapperProps>> = ({
  title,
  reportSectionId,
  subtitle,
  infoTooltip,
  renderCustom,
  isExportable,
  toggleExportDialog,
  renderSettingsDropdown,
  width,
  hasFullHeight,
  isBeta,
  isNew,
  children,
}) => {
  const trackAnalytics = useAnalyticsTracking();
  const theme = useUserTheme();

  const onSettingsClick = () => {
    trackAnalytics(InterfaceEventsEnum.ANALYTICS_METRIC_LIST_CLICK, {
      report_section: reportSectionId,
    });
  };

  return (
    <ReportGridItem $width={width} $hasFullHeight={hasFullHeight}>
      {title && (
        <StyledReportElementTitleWrapper
          $hasExport={Boolean(toggleExportDialog)}
          $hasSettings={Boolean(renderSettingsDropdown)}
        >
          <StyledReportElementTitle>{title}</StyledReportElementTitle>
          {maybeRenderInfoButton(infoTooltip)}
          {maybeRenderMetricPillAndTooltip(theme, isBeta, isNew)}
          {maybeRenderCustom(renderCustom)}
          {maybeRenderExportButton(toggleExportDialog, isExportable)}
          {maybeRenderSettingsButton(onSettingsClick, renderSettingsDropdown)}
        </StyledReportElementTitleWrapper>
      )}
      {subtitle && <StyledReportElementSubtitle>{subtitle}</StyledReportElementSubtitle>}
      {children}
    </ReportGridItem>
  );
};

/*
 * Helpers.
 */

const maybeRenderInfoButton = (tooltip: AnalyticsHelpTooltip | undefined) => {
  if (!tooltip) {
    return null;
  }

  return <StyledReportElementInfoButton tooltip={tooltip} />;
};

const maybeRenderCustom = (renderCustom?: Renderer) => {
  if (!renderCustom) {
    return null;
  }

  return <StyledReportElementCustomWrapper>{renderCustom()}</StyledReportElementCustomWrapper>;
};

const maybeRenderExportButton = (toggleExportDialog?: Handler, isExportable?: boolean) => {
  if (isUndefined(toggleExportDialog) || !isExportable) {
    return null;
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledReportElementExportButtonWrapper>
          <IconButton
            iconName="export"
            label={<Trans id="analytics-export-button">Export</Trans>}
            color={theme.greys.shade70}
            onClick={toggleExportDialog}
          />
        </StyledReportElementExportButtonWrapper>
      )}
    </UserThemeConsumer>
  );
};

const maybeRenderSettingsButton = (
  onSettingsOpen: Handler,
  renderSettingsDropdown?: RendererOf<PopoverTransferProps>,
) => {
  if (!renderSettingsDropdown) {
    return null;
  }

  const interactionId = 'analytics-columns-filters';
  const interactionProps = buildInteractionIdProps(interactionId);

  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledReportElementSettingsButton
          renderButton={() => (
            <div {...interactionProps}>
              <IconButton
                iconName="gear"
                label={<Trans id="analytics-settings-button">Customize metrics</Trans>}
                color={theme.greys.shade70}
              />
            </div>
          )}
          renderModal={renderSettingsDropdown}
          onPopoverToggle={(isOpen) => (isOpen ? onSettingsOpen() : null)}
        />
      )}
    </UserThemeConsumer>
  );
};
